import { useState, useEffect } from 'react';
import api from '@/api';
import {
  Shield, CheckCircle2, Clock, Lock, XCircle,
  ChevronRight, Loader2, AlertTriangle, Plus,
  Target, Zap, FileCheck, TrendingUp,
} from 'lucide-react';

const gateIcons = {
  locked: Lock,
  pending: Clock,
  in_review: Loader2,
  approved: CheckCircle2,
  rejected: XCircle,
};

const gateColors = {
  locked: { ring: 'border-border', bg: 'bg-muted/50', text: 'text-muted-foreground', label: 'Locked' },
  pending: { ring: 'border-amber-700', bg: 'bg-amber-100 dark:bg-amber-950/20', text: 'text-amber-700 dark:text-amber-400', label: 'Ready' },
  in_review: { ring: 'border-blue-700', bg: 'bg-blue-100 dark:bg-blue-950/20', text: 'text-blue-700 dark:text-blue-400', label: 'In Review' },
  approved: { ring: 'border-emerald-600', bg: 'bg-emerald-100 dark:bg-emerald-950/20', text: 'text-emerald-700 dark:text-emerald-400', label: 'Approved' },
  rejected: { ring: 'border-red-700', bg: 'bg-red-100 dark:bg-red-950/20', text: 'text-red-700 dark:text-red-400', label: 'Rejected' },
};

const milestoneStatusConfig = {
  not_triggered: { color: 'text-muted-foreground', bg: 'bg-muted/30', icon: Clock, label: 'Waiting' },
  triggered: { color: 'text-amber-500', bg: 'bg-amber-100 dark:bg-amber-950/20', icon: Zap, label: 'Triggered' },
  acknowledged: { color: 'text-emerald-500', bg: 'bg-emerald-100 dark:bg-emerald-950/20', icon: CheckCircle2, label: 'Acknowledged' },
};

const triggerLabels = {
  submittal_count: 'Submittals uploaded',
  submittal_approval_pct: 'Submittal approval %',
  doc_completion_pct: 'Document completion %',
  asset_count: 'Assets extracted',
  rfi_resolved_pct: 'RFIs resolved %',
  division_complete: 'Division docs complete %',
};

export default function QualityGates({ projectId }) {
  const [subTab, setSubTab] = useState('gates'); // 'gates' | 'milestones'
  const [gatesData, setGatesData] = useState(null);
  const [milestoneData, setMilestoneData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [reviewForm, setReviewForm] = useState({ gate: null, notes: '' });
  const [showAddMilestone, setShowAddMilestone] = useState(false);
  const [newMilestone, setNewMilestone] = useState({ name: '', description: '', trigger_type: 'doc_completion_pct', trigger_threshold: 80 });

  const fetchData = async () => {
    try {
      const [gRes, mRes] = await Promise.all([
        api.get(`/closeout/projects/${projectId}/gates`),
        api.get(`/closeout/projects/${projectId}/milestones`),
      ]);
      setGatesData(gRes.data);
      setMilestoneData(mRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAction = async (gateNumber, status) => {
    setUpdating(gateNumber);
    try {
      await api.put(`/closeout/projects/${projectId}/gates/${gateNumber}`, {
        status,
        notes: reviewForm.notes || null,
      });
      setReviewForm({ gate: null, notes: '' });
      await fetchData();
    } catch (err) { console.error(err); }
    finally { setUpdating(null); }
  };

  const handleAcknowledge = async (milestoneId) => {
    try {
      await api.post(`/closeout/projects/${projectId}/milestones/${milestoneId}/acknowledge`, {
        audit_result: 'pass',
        notes: '',
      });
      await fetchData();
    } catch (err) { console.error(err); }
  };

  const handleAddMilestone = async () => {
    try {
      await api.post(`/closeout/projects/${projectId}/milestones`, newMilestone);
      setShowAddMilestone(false);
      setNewMilestone({ name: '', description: '', trigger_type: 'doc_completion_pct', trigger_threshold: 80 });
      await fetchData();
    } catch (err) { console.error(err); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-32">
      <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const gates = gatesData?.gates || [];
  const completion_pct = gatesData?.completion_pct || 0;
  const milestones = milestoneData?.milestones || [];
  const stats = milestoneData?.stats || {};

  return (
    <div data-testid="quality-gates">
      {/* Sub-tabs */}
      <div className="flex gap-1 mb-5">
        {[
          { id: 'gates', label: 'Quality Gates', icon: Shield },
          { id: 'milestones', label: 'Audit Milestones', icon: Target },
        ].map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)}
            data-testid={`gates-subtab-${t.id}`}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-heading uppercase tracking-wider rounded-sm transition-colors ${
              subTab === t.id ? 'bg-blue-100 dark:bg-blue-900/20 text-primary border border-primary/30' : 'text-muted-foreground hover:text-foreground/80'
            }`}>
            <t.icon className="w-3 h-3" /> {t.label}
          </button>
        ))}
      </div>

      {subTab === 'gates' ? (
        <>
          {/* Completion bar */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Overall Completion</span>
              <span className="font-mono text-sm text-foreground/80">{completion_pct}%</span>
            </div>
            <div className="h-2 bg-secondary rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-orange-600 to-emerald-500 transition-all duration-700 rounded-full"
                style={{ width: `${Math.min(completion_pct, 100)}%` }} />
            </div>
            <div className="relative h-4 mt-1">
              {gates.map(g => (
                <div key={g.gate_number}
                  className="absolute top-0 transform -translate-x-1/2"
                  style={{ left: `${g.required_completion_pct}%` }}>
                  <div className="w-px h-3 bg-slate-700" />
                  <span className="text-[8px] font-mono text-muted-foreground whitespace-nowrap">G{g.gate_number} ({g.required_completion_pct}%)</span>
                </div>
              ))}
            </div>
          </div>

          {/* Gate Cards */}
          <div className="flex gap-4">
            {gates.map((g, i) => {
              const status = g.status;
              const style = gateColors[status] || gateColors.locked;
              const Icon = gateIcons[status] || Lock;
              const isActive = status === 'pending' || status === 'in_review' || status === 'rejected';

              return (
                <div key={g.gate_number} className="flex items-center gap-4 flex-1">
                  <div className={`flex-1 border-2 rounded-sm p-4 transition-colors ${style.ring} ${style.bg}`}
                    data-testid={`gate-${g.gate_number}`}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-10 h-10 rounded-full border-2 ${style.ring} flex items-center justify-center`}>
                        <Icon className={`w-5 h-5 ${style.text} ${status === 'in_review' ? 'animate-spin' : ''}`} />
                      </div>
                      <div>
                        <p className="text-sm font-heading font-bold text-foreground">Gate {g.gate_number}</p>
                        <p className="text-[10px] text-muted-foreground">{g.gate_name}</p>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mb-3 leading-relaxed">{g.description}</p>
                    <div className="flex items-center justify-between text-xs mb-3">
                      <span className="text-muted-foreground">Required: {g.required_completion_pct}%</span>
                      <span className={style.text}>{style.label}</span>
                    </div>
                    {g.reviewer_name && (
                      <div className="text-xs text-muted-foreground mb-3 p-2 bg-card rounded-sm">
                        <p>Reviewed by: <span className="text-foreground/80">{g.reviewer_name}</span></p>
                        {g.review_date && <p>Date: <span className="font-mono text-muted-foreground">{new Date(g.review_date).toLocaleDateString()}</span></p>}
                        {g.notes && <p className="mt-1 text-muted-foreground">{g.notes}</p>}
                      </div>
                    )}
                    {isActive && (
                      <div className="space-y-2">
                        {reviewForm.gate === g.gate_number ? (
                          <div className="space-y-2">
                            <textarea
                              className="w-full bg-card border border-border rounded-sm px-2 py-1.5 text-xs text-foreground resize-none h-16 focus:outline-none focus:border-primary/50"
                              placeholder="Review notes (optional)"
                              value={reviewForm.notes}
                              onChange={e => setReviewForm(f => ({ ...f, notes: e.target.value }))}
                            />
                            <div className="flex gap-2">
                              <button onClick={() => handleAction(g.gate_number, 'approved')}
                                disabled={updating === g.gate_number}
                                data-testid={`approve-gate-${g.gate_number}`}
                                className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs rounded-sm transition-colors">
                                {updating === g.gate_number ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Approve
                              </button>
                              <button onClick={() => handleAction(g.gate_number, 'rejected')}
                                disabled={updating === g.gate_number}
                                className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-red-600 hover:bg-red-500 text-white text-xs rounded-sm transition-colors">
                                <XCircle className="w-3 h-3" /> Reject
                              </button>
                              <button onClick={() => setReviewForm({ gate: null, notes: '' })}
                                className="px-2 py-1.5 text-xs text-muted-foreground hover:text-foreground/80">Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <button onClick={() => setReviewForm({ gate: g.gate_number, notes: '' })}
                            data-testid={`review-gate-${g.gate_number}`}
                            className="w-full px-3 py-2 text-xs font-heading uppercase tracking-wider bg-primary/10 text-primary border border-primary/30 rounded-sm hover:bg-primary/15 transition-colors">
                            Start Review
                          </button>
                        )}
                      </div>
                    )}
                    {status === 'locked' && !g.can_unlock && (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Lock className="w-3 h-3" />
                        {i > 0 ? `Requires Gate ${g.gate_number - 1} approval` : `Requires ${g.required_completion_pct}% completion`}
                      </div>
                    )}
                  </div>
                  {i < gates.length - 1 && (
                    <ChevronRight className={`w-5 h-5 shrink-0 ${status === 'approved' ? 'text-emerald-500' : 'text-muted-foreground'}`} />
                  )}
                </div>
              );
            })}
          </div>
        </>
      ) : (
        /* Milestones Tab */
        <div className="space-y-5" data-testid="audit-milestones">
          {/* Stats Row */}
          <div className="grid grid-cols-4 gap-3">
            {[
              { label: 'Submittals', value: stats.submittal_count || 0, sub: `${stats.submittal_approval_pct || 0}% approved`, color: 'text-blue-700 dark:text-blue-400' },
              { label: 'Assets', value: stats.asset_count || 0, sub: 'extracted', color: 'text-cyan-400' },
              { label: 'Documents', value: `${stats.doc_completion_pct || 0}%`, sub: `${stats.doc_received || 0} of ${stats.doc_total || 0}`, color: 'text-emerald-400' },
              { label: 'RFIs', value: stats.rfi_total || 0, sub: `${stats.rfi_resolved_pct || 0}% resolved`, color: 'text-amber-400' },
            ].map((s, i) => (
              <div key={i} className="bg-card border border-border rounded-sm p-3">
                <p className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground">{s.label}</p>
                <p className={`font-mono text-lg font-bold mt-0.5 ${s.color}`}>{s.value}</p>
                <p className="text-[10px] text-muted-foreground">{s.sub}</p>
              </div>
            ))}
          </div>

          {/* Add Milestone */}
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Project Milestones</p>
            <button onClick={() => setShowAddMilestone(!showAddMilestone)}
              data-testid="add-milestone-btn"
              className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-heading uppercase tracking-wider text-primary border border-primary/30 rounded-sm hover:bg-primary/10 transition-colors">
              <Plus className="w-3 h-3" /> Custom Milestone
            </button>
          </div>

          {/* Add Milestone Form */}
          {showAddMilestone && (
            <div className="p-4 bg-card border border-border rounded-sm space-y-3" data-testid="add-milestone-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase">Name</label>
                  <input value={newMilestone.name}
                    onChange={e => setNewMilestone(m => ({ ...m, name: e.target.value }))}
                    className="w-full mt-0.5 px-2.5 py-1.5 text-xs bg-background border border-border rounded-sm focus:outline-none focus:border-primary"
                    placeholder="e.g., All RFIs Resolved" data-testid="milestone-name-input" />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase">Trigger Type</label>
                  <select value={newMilestone.trigger_type}
                    onChange={e => setNewMilestone(m => ({ ...m, trigger_type: e.target.value }))}
                    className="w-full mt-0.5 px-2.5 py-1.5 text-xs bg-background border border-border rounded-sm focus:outline-none focus:border-primary"
                    data-testid="milestone-trigger-select">
                    {Object.entries(triggerLabels).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase">Threshold</label>
                  <input type="number" value={newMilestone.trigger_threshold}
                    onChange={e => setNewMilestone(m => ({ ...m, trigger_threshold: parseFloat(e.target.value) || 0 }))}
                    className="w-full mt-0.5 px-2.5 py-1.5 text-xs bg-background border border-border rounded-sm focus:outline-none focus:border-primary"
                    data-testid="milestone-threshold-input" />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase">Description</label>
                  <input value={newMilestone.description}
                    onChange={e => setNewMilestone(m => ({ ...m, description: e.target.value }))}
                    className="w-full mt-0.5 px-2.5 py-1.5 text-xs bg-background border border-border rounded-sm focus:outline-none focus:border-primary"
                    placeholder="Optional description" />
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={handleAddMilestone} disabled={!newMilestone.name}
                  data-testid="save-milestone-btn"
                  className="px-4 py-1.5 text-xs bg-primary text-primary-foreground rounded-sm hover:opacity-90 transition-opacity disabled:opacity-50">
                  Save
                </button>
                <button onClick={() => setShowAddMilestone(false)}
                  className="text-xs text-muted-foreground hover:text-foreground">Cancel</button>
              </div>
            </div>
          )}

          {/* Milestone Timeline */}
          <div className="space-y-0">
            {milestones.map((m, i) => {
              const sc = milestoneStatusConfig[m.status] || milestoneStatusConfig.not_triggered;
              const Icon = sc.icon;
              const progress = m.trigger_threshold > 0 ? Math.min((m.current_value / m.trigger_threshold) * 100, 100) : 0;
              const isTriggered = m.status === 'triggered';

              return (
                <div key={m.id} className="flex gap-3" data-testid={`milestone-${i}`}>
                  {/* Timeline connector */}
                  <div className="flex flex-col items-center w-8 shrink-0">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${
                      m.status === 'acknowledged' ? 'border-emerald-500 bg-emerald-950/30' :
                      m.status === 'triggered' ? 'border-amber-500 bg-amber-950/30' :
                      'border-border bg-muted/30'
                    }`}>
                      <Icon className={`w-3 h-3 ${sc.color}`} />
                    </div>
                    {i < milestones.length - 1 && (
                      <div className={`w-px flex-1 min-h-[24px] ${
                        m.status === 'acknowledged' ? 'bg-emerald-700' : 'bg-border'
                      }`} />
                    )}
                  </div>

                  {/* Milestone Card */}
                  <div className={`flex-1 p-3 rounded-sm border mb-3 transition-colors ${
                    isTriggered ? 'border-amber-700 bg-amber-950/10' :
                    m.status === 'acknowledged' ? 'border-emerald-800/30 bg-emerald-950/10' :
                    'border-border bg-card'
                  }`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs font-medium text-foreground">{m.name}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{m.description}</p>
                      </div>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-sm ${sc.bg} ${sc.color}`}>
                        {sc.label}
                      </span>
                    </div>

                    {/* Progress bar */}
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all duration-500 ${
                          m.status === 'acknowledged' ? 'bg-emerald-500' :
                          progress >= 100 ? 'bg-amber-500' : 'bg-blue-500'
                        }`} style={{ width: `${progress}%` }} />
                      </div>
                      <span className="text-[9px] font-mono text-muted-foreground shrink-0">
                        {typeof m.current_value === 'number' ? Math.round(m.current_value) : m.current_value} / {m.trigger_threshold}
                        {m.trigger_type.includes('pct') ? '%' : ''}
                      </span>
                    </div>

                    {/* Trigger info */}
                    <p className="text-[9px] text-muted-foreground mt-1">
                      {triggerLabels[m.trigger_type] || m.trigger_type} {'\u2265'} {m.trigger_threshold}{m.trigger_type.includes('pct') ? '%' : ''}
                    </p>

                    {/* Acknowledge button for triggered milestones */}
                    {isTriggered && (
                      <button onClick={() => handleAcknowledge(m.id)}
                        data-testid={`ack-milestone-${i}`}
                        className="mt-2 flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-heading uppercase tracking-wider bg-amber-600 hover:bg-amber-500 text-white rounded-sm transition-colors">
                        <CheckCircle2 className="w-3 h-3" /> Acknowledge & Audit
                      </button>
                    )}

                    {/* Acknowledged info */}
                    {m.status === 'acknowledged' && m.acknowledged_by && (
                      <p className="text-[9px] text-emerald-500 mt-1.5">
                        Acknowledged by {m.acknowledged_by} on {new Date(m.acknowledged_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
